/**
 * Fliplab — Globo Form Builder repeater sync
 *
 * Globo renders every `repeater` field as its own independent widget: each one
 * gets its own "Add another…" button and its own per-input remove button. When
 * several repeaters sit side by side in one row (Custom Door Orders: Length /
 * Width / Quantity) the shopper has to press Add three times, and the columns
 * drift out of alignment the moment one is removed.
 *
 * This links every group of repeaters that share a `.globo-form-row` so that:
 *   - one Add button appends a row to ALL repeaters in the group
 *   - one Remove button deletes the SAME index from ALL repeaters in the group
 *   - the group can never drop below a single row
 *
 * It does not re-implement Globo's DOM work — it calls Globo's own
 * `addInputs()` / `removeInputs()` once per column, so the limit handling,
 * `gfb__no-label` class and step-wizard height recalculation all stay native.
 *
 * Presentation (hiding the duplicate buttons) lives in `_custom.css` under
 * `.fliplab-gfb-synced`: the Add button and the Remove buttons both survive on
 * the last column. It is deliberately class-based, never inline styles,
 * because Globo writes and clears inline `style` on `.gfb__repeater` itself
 * when a repeater hits its row limit.
 */
(function () {
  'use strict';

  var GROUP_CLASS = 'fliplab-gfb-synced';
  var SINGLE_CLASS = 'fliplab-gfb-synced--single';
  var LEAD_CLASS = 'fliplab-gfb-synced__lead';
  var TAIL_CLASS = 'fliplab-gfb-synced__tail';
  var CONTROL_SELECTOR = '.globo-form-control[data-type="repeater"]';
  var ROW_SELECTOR = '.gfb__form-inputs > .globo-form-input';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function controlsIn(row) {
    return toArray(row.querySelectorAll(CONTROL_SELECTOR));
  }

  function inputRows(control) {
    return toArray(control.querySelectorAll(ROW_SELECTOR));
  }

  /** The repeater group the given element belongs to, or null. */
  function groupFor(element) {
    if (!element || !element.closest) return null;
    var control = element.closest(CONTROL_SELECTOR);
    if (!control) return null;
    var row = control.closest('.' + GROUP_CLASS);
    if (!row) return null;
    var controls = controlsIn(row);
    return controls.length > 1 ? { row: row, controls: controls } : null;
  }

  function addButton(control) {
    return control.querySelector('.gfb__repeater .gfb__add-button');
  }

  /** Append one row to a single column, using Globo's own logic where possible. */
  function addRowTo(control) {
    var button = addButton(control);
    if (button && window.Globo && Globo.FormBuilder && typeof Globo.FormBuilder.addInputs === 'function') {
      Globo.FormBuilder.addInputs(button);
      return;
    }

    // Fallback: mirror what addInputs() does, in case the app API moves.
    var container = control.querySelector('.gfb__form-inputs');
    if (!container || !container.firstElementChild) return;
    var clone = container.firstElementChild.cloneNode(true);
    clone.classList.add('gfb__no-label');
    var field = clone.querySelector('input, textarea, select');
    if (field) field.value = '';
    container.appendChild(clone);
  }

  /** Remove the row at `index` from a single column. */
  function removeRowFrom(control, index) {
    var rows = inputRows(control);
    var row = rows[index];
    if (!row) return;

    var button = row.querySelector('.gfb__repeater-remove');
    if (button && window.Globo && Globo.FormBuilder && typeof Globo.FormBuilder.removeInputs === 'function') {
      Globo.FormBuilder.removeInputs(button);
      return;
    }

    row.remove();
    // removeInputs() also un-hides the Add button once back under the limit.
    var repeater = control.querySelector('.gfb__repeater');
    if (repeater) repeater.removeAttribute('style');
  }

  /**
   * Bring every column to the same row count and refresh the single-row state.
   * Runs after each add/remove, and once on load, so a group that was already
   * lopsided (a click that landed before this script booted) self-corrects.
   */
  function normalise(group) {
    var target = group.controls.reduce(function (max, control) {
      return Math.max(max, inputRows(control).length);
    }, 0);

    if (target === 0) return;

    group.controls.forEach(function (control) {
      var count = inputRows(control).length;
      for (var i = count; i < target; i++) addRowTo(control);
    });

    group.row.classList.toggle(SINGLE_CLASS, target <= 1);
  }

  function onAdd(group) {
    group.controls.forEach(addRowTo);
    normalise(group);
  }

  function onRemove(group, clickedRow) {
    var lead = clickedRow.closest(CONTROL_SELECTOR);
    var index = inputRows(lead).indexOf(clickedRow);
    if (index < 0) return;

    // A repeater group always keeps at least one row.
    if (inputRows(lead).length <= 1) return;

    group.controls.forEach(function (control) {
      removeRowFrom(control, index);
    });
    normalise(group);
  }

  /**
   * Capture phase on the document: stopping propagation here means the event
   * never reaches the button, so Globo's inline `onclick` never fires and we
   * stay in sole control. This also covers rows cloned in later, which carry
   * the same inline handlers.
   */
  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var add = target.closest('.gfb__add-button');
    if (add) {
      var addGroup = groupFor(add);
      if (!addGroup) return;
      event.preventDefault();
      event.stopPropagation();
      onAdd(addGroup);
      return;
    }

    var remove = target.closest('.gfb__repeater-remove');
    if (remove) {
      var removeGroup = groupFor(remove);
      if (!removeGroup) return;
      event.preventDefault();
      event.stopPropagation();
      var clickedRow = remove.closest('.globo-form-input');
      if (clickedRow) onRemove(removeGroup, clickedRow);
    }
  }

  /** Tag rows that hold more than one repeater, and mark the lead column. */
  function markGroups(root) {
    var scope = root && root.querySelectorAll ? root : document;
    toArray(scope.querySelectorAll('.globo-form-row')).forEach(function (row) {
      var controls = controlsIn(row);
      if (controls.length < 2 || row.classList.contains(GROUP_CLASS)) return;

      row.classList.add(GROUP_CLASS);
      // Both the Add button and the Remove buttons are kept on the LAST column,
      // so every control sits on the right-hand edge of the row instead of
      // repeating in every column.
      var last = controls[controls.length - 1];
      last.classList.add(LEAD_CLASS);
      last.classList.add(TAIL_CLASS);
      normalise({ row: row, controls: controls });
    });
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('globo.formbuilder.form.loaded', function (event) {
    markGroups(event.detail && event.detail.form);
    markGroups(document);
  });

  // The form is injected asynchronously, and may be re-rendered (step changes,
  // reset). Watch until the first group appears, then keep watching cheaply.
  if (window.MutationObserver) {
    new MutationObserver(function () {
      markGroups(document);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  markGroups(document);
})();
